import { FunctionComponent, useMemo } from 'react';
import { FaRegBuilding } from 'react-icons/fa';
import { FaChartLine, FaFileLines } from 'react-icons/fa6';
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  AreaChart,
  Area,
} from 'recharts';
import { Tooltip as MuiTooltip } from '@mui/material';
import { AUMTime } from '../types/dashboard.types';
import { getSparklineDomain } from '../utils/chart.utils';

interface TotalFundsProps {
  totalFunds: number;
  totalReportedFunds: number;
  fundsList: AUMTime[];
}

const TotalFundsCard: FunctionComponent<TotalFundsProps> = ({
  totalFunds,
  totalReportedFunds,
  fundsList,
}) => {
  const chartData = useMemo(() => {
    return (fundsList || []).map((d) => ({
      date: d.date,
      funds: d.total_fund_count ?? d.reported_fund_count,
      reported: d.reported_fund_count,
    }));
  }, [fundsList]);

  const yDomain = useMemo(
    () => getSparklineDomain(chartData.map((d) => d.funds)),
    [chartData],
  );

  const show = fundsList && fundsList.length >= 2;

  return (
    <div className="relative mt-4 flex min-w-0 flex-1 flex-col border-t border-gray-200 pt-4 sm:mt-0 sm:h-full sm:border-t-0 sm:pl-6 sm:pt-0">
      {/* Divider line for layout */}
      <div className="sm:absolute sm:left-0 sm:top-0 sm:h-full sm:border-l sm:border-gray-200"></div>

      <div className="grid min-w-0 grid-cols-2 gap-x-4">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
            <FaRegBuilding className="shrink-0 text-blue-500" />
            <span className="truncate">Funds</span>
          </div>
          <div className="mt-1.5 text-lg font-bold tracking-tight text-gray-900">
            {totalFunds}
          </div>
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
            <FaFileLines className="shrink-0 text-green-500" />
            <span className="truncate">Reported</span>
          </div>
          <div className="mt-1.5 text-lg font-bold tracking-tight text-gray-900">
            {totalReportedFunds}
          </div>
        </div>
      </div>

      {/* In-book funds vs funds that reported that day */}
      <div className="mt-1 h-16">
        {show ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              className="rounded border border-gray-200 bg-white"
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            >
              <XAxis dataKey="date" hide />
              <YAxis hide domain={yDomain} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const point = payload[0].payload as {
                    funds: number;
                    reported: number;
                  };
                  return (
                    <div
                      style={{
                        margin: 0,
                        padding: 10,
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <p style={{ margin: 0 }}>Date: {label}</p>
                      <ul
                        style={{
                          margin: 0,
                          padding: 0,
                          listStyle: 'none',
                        }}
                      >
                        <li style={{ color: '#3b82f6' }}>
                          Funds : {point.funds} funds
                        </li>
                        <li style={{ color: '#16a34a' }}>
                          Reported : {point.reported} reported
                        </li>
                      </ul>
                    </div>
                  );
                }}
                position={{ y: 50 }}
              />
              <Area
                type="monotone"
                dataKey="funds"
                name="Funds"
                stroke="#3b82f6"
                fill="#3b82f6"
                fillOpacity={0.25}
                strokeWidth={2}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <MuiTooltip title="Chart available for data over 1 day" arrow>
            <div className="bg-gray-25 flex h-full items-center justify-center rounded border border-gray-100">
              <FaChartLine className="text-xs text-gray-400" />
            </div>
          </MuiTooltip>
        )}
      </div>
    </div>
  );
};

export default TotalFundsCard;
